/**
 * mobileBootstrap — one-shot startup hook called from main.tsx BEFORE
 * ReactDOM.createRoot().render(). Handles tasks that must run exactly once
 * and need to await native plugin calls.
 *
 * On web: rehydrates the refresh token from localStorage and (C16) runs a
 * refresh-first round trip so the accessToken is in memory by first render.
 *
 * On native (iOS/Android via Capacitor):
 *   1. Rehydrate refresh token from secureStorage (Keychain / EncryptedSharedPreferences).
 *   2. C16: refresh-first bootstrap — POST /auth/refresh once with the
 *      persisted refresh token. Bounded by a 5s timeout so a slow network
 *      cannot freeze the splash screen. Failures are silent: secureStorage is
 *      cleared and the router will redirect to /login on first protected
 *      route hit.
 *   3. Configure status bar style (Light = dark text, dark icons).
 *   4. Wire Android hardware back button → navigate(-1) when possible,
 *      App.minimizeApp() at root.
 *   5. Hide the splash screen once the React tree is mounted (caller pattern:
 *      bootstrap awaits, then createRoot.render(); React paints first frame
 *      and *immediately after* we hide the splash).
 *   6. Log device info to console (dev mode only, never PII).
 *
 * All native plugin calls are wrapped in try/catch so a single failing call
 * never blocks the app boot — splash will still hide on a timeout fallback.
 *
 * Regardless of any failure, `_hasHydrated` is flipped to `true` in the
 * `finally` block so React components that gate on the boot signal never
 * stall forever.
 */
import { bootstrapAuth, hydrateRefreshTokenFromStorage } from '../services/api';
import { useAuthStore } from '../stores/auth.store';
import { isNativePlatform } from './platform';

interface BootstrapOptions {
  /** When true, log debug info to console. Defaults to import.meta.env.DEV. */
  verbose?: boolean;
}

/** Hard cap for the pre-React refresh round trip (ms). After this, boot
 * proceeds with whatever auth state is currently in memory; runtime 401 +
 * mutex will eventually catch up if the network recovers. */
const BOOTSTRAP_AUTH_TIMEOUT_MS = 5000;

/**
 * Runs auth + native shell initialization. Idempotent — calling twice is
 * harmless (rehydration is a get, plugin listeners are added once, splash
 * hide is no-op after first call).
 */
export async function mobileBootstrap(options: BootstrapOptions = {}): Promise<void> {
  const verbose = options.verbose ?? Boolean(import.meta.env.DEV);

  try {
    // 1. Rehydrate refresh token (both web + native paths).
    try {
      await hydrateRefreshTokenFromStorage();
    } catch (err) {
      if (verbose) console.warn('[mobileBootstrap] refresh token rehydrate failed', err);
    }

    // 2. C16: refresh-first round trip with hard timeout. We race the bootstrap
    // call against a 5s timer so a hung network cannot keep the splash up
    // indefinitely. The mutex inside ApiService guarantees that even if a
    // runtime 401 fires concurrently, only one POST /auth/refresh hits the
    // wire.
    try {
      const result = await Promise.race<'authenticated' | 'unauthenticated'>([
        bootstrapAuth(),
        new Promise<'unauthenticated'>((resolve) =>
          setTimeout(() => resolve('unauthenticated'), BOOTSTRAP_AUTH_TIMEOUT_MS)
        ),
      ]);
      if (verbose) console.info('[mobileBootstrap] auth result:', result);
    } catch (err) {
      if (verbose) console.warn('[mobileBootstrap] bootstrapAuth failed', err);
    }

    // Everything below this is native-only.
    if (!isNativePlatform()) return;

    try {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      await StatusBar.setStyle({ style: Style.Light });
    } catch (err) {
      if (verbose) console.warn('[mobileBootstrap] status bar setup skipped', err);
    }

    try {
      const { App } = await import('@capacitor/app');
      // Note: useBackButton hook also wires the back button per-route; this is
      // the safety net for the very first paint before any hook has mounted.
      // Listener is automatically deduped by Capacitor under the hood.
      await App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack && typeof window !== 'undefined') {
          window.history.back();
        } else {
          App.minimizeApp().catch(() => undefined);
        }
      });
    } catch (err) {
      if (verbose) console.warn('[mobileBootstrap] back button listener skipped', err);
    }

    // Deep-link wiring (`appUrlOpen` + cold-start launch URL) is handled by
    // `useDeepLinkListener()` inside the React tree (it needs `useNavigate`).
    // See `apps/web/src/hooks/useDeepLinkListener.ts`. Do NOT add the listener
    // here too — bootstrap runs once before mount, the hook re-runs cleanly on
    // HMR and integrates with router state.

    if (verbose) {
      try {
        const { Device } = await import('@capacitor/device');
        const info = await Device.getInfo();
        // Only log non-PII fields. operatingSystem/osVersion/platform/manufacturer
        // are environmental, not user-identifying.
        console.info('[mobileBootstrap] device', {
          platform: info.platform,
          operatingSystem: info.operatingSystem,
          osVersion: info.osVersion,
          manufacturer: info.manufacturer,
          model: info.model,
          webViewVersion: info.webViewVersion,
        });
      } catch (err) {
        console.warn('[mobileBootstrap] device info skipped', err);
      }
    }

    // Hide splash AFTER all of the above, so the user does not see a flash of
    // unstyled content. We give React one tick to mount before hiding.
    try {
      const { SplashScreen } = await import('@capacitor/splash-screen');
      // Defer to next tick so the React tree gets at least one paint.
      setTimeout(() => {
        SplashScreen.hide().catch(() => undefined);
      }, 0);
    } catch (err) {
      if (verbose) console.warn('[mobileBootstrap] splash hide skipped', err);
    }
  } finally {
    // C16: ALWAYS flip _hasHydrated, even if every step above threw. Components
    // that gate on the boot signal (e.g. DashboardPage onboarding check) must
    // never wait forever. If auth failed, the store is already cleared and the
    // router will redirect to /login on the first protected route hit.
    useAuthStore.getState().setHasHydrated(true);
  }
}
